import React, { useState, useEffect } from "react";
import {
	Button,
	Card,
	Col,
	CardBody,
	CardHeader,
	CardTitle,
	Row,
	Form,
} from "reactstrap";
import formNavigationUtils from "utils/formNavigationUtils";
import { showApiError } from "../../../../../utils/errorRoutingUtils";
import {
	CheckBoxElement,
	CurrencyInput,
	IntegerInput,
} from "../../../../forms/components/InputElement";
import {
	saveFailAlert,
	saveSuccessAlert,
	retrieveFailAlert,
} from "../../../../../utils/alertUtils";
import {
	getLeaseDefaults,
	saveLeaseDefaults,
} from "../../../../../api/DealAPI";
import { useFromUser } from "../../../../../stores/LocalStorageHelper";
import useCurrentLeaseStore from "../../../../../stores/LeaseStore";
import shallow from "zustand/shallow";

// Safe number coercion
const n = (v, fallback = 0) => {
	const num = typeof v === "string" ? parseFloat(v) : Number(v);
	return Number.isFinite(num) ? num : fallback;
};

const LeaseSettings = ({ isDeal }) => {
	console.log(isDeal);
	const locationID = useFromUser("locationID");
	const dealerID = useFromUser("dealerID");

	// --- boolean toggles ---
	const [capBankFee, setCapBankFee] = useState(false);
	const [capDMVFee, setCapDMVFee] = useState(false);
	const [capDocFee, setCapDocFee] = useState(false);
	const [capSalesTax, setCapSalesTax] = useState(false);
	const [capMiscFee, setCapMiscFee] = useState(false);
	const [capOtherfee, setCapOtherfee] = useState(false);
	const [capTaxOnReduction, setCapTaxOnReduction] = useState(false);
	const [capFirstPmt, setCapFirstPmt] = useState(false);
	const [capWarranty, setCapWarranty] = useState(false);
	const [capGap, setCapGap] = useState(false);
	const [capDispositionFee, setCapDispositionFee] = useState(false);

	// --- numeric fields ---
	const [moneyFactor, setMoneyFactor] = useState(0);
	const [allowableMilesPerYear, setAllowableMilesPerYear] = useState(0);
	const [capReduction, setCapReduction] = useState(0);
	const [bankFee, setBankFee] = useState(0);
	const [capSmogFees, setCapSmogFees] = useState(false);
	const [additionalFee, setAdditionalFee] = useState(0);
	const [terminationFee, setTerminationFee] = useState(false);
	const [dispositionFee, setDispositionFee] = useState(false);
	const [proRataRent, setProRataRent] = useState(0);
	const [proRataSalesTax, setProRataSalesTax] = useState(0);
	const [securityDeposit, setSecurityDeposit] = useState(0);
	const [id, setId] = useState(null);
	const lease = useCurrentLeaseStore((state) => ({ ...state }), shallow);
	// Load defaults (inline effect ⇒ no exhaustive-deps warning)
	useEffect(() => {
		if (isDeal) {
			setCapBankFee(lease.capBankFee);
			setCapDMVFee(lease.capDMVFee);
			setCapDocFee(lease.capDocFee);
			setCapSalesTax(lease.capSalesTax);
			setCapMiscFee(lease.capMiscFee);
			setCapOtherfee(lease.capOtherfee);
			setCapTaxOnReduction(lease.capTaxOnReduction);
			setCapFirstPmt(lease.capFirstPmt);
			setCapWarranty(lease.capWarranty);
			setCapGap(lease.capGap);
			setCapSmogFees(lease.capSmogFees);
			setCapDispositionFee(lease.capDispositionFee);

			setMoneyFactor(n(lease.moneyFactor, 0));
			setAllowableMilesPerYear(n(lease.allowableMilesPerYear, 0));
			setCapReduction(n(lease.capReduction, 0));
			setBankFee(n(lease.bankFee, 0));
			setAdditionalFee(n(lease.additionalFee, 0));
			setTerminationFee(lease.terminationFee, 0);
			setDispositionFee(lease.disposition, 0);
			setProRataRent(n(lease.proRataRent, 0));
			setProRataSalesTax(n(lease.proRataSalesTax, 0));
			setSecurityDeposit(n(lease.securityDeposit, 0));
		} else {
			getLeaseDefaults(locationID).then(
				(res) => {
					console.log(res);
					const d = res?.data?.content || {};
					setId(d.id);
					setCapBankFee(Boolean(d.capBankFee));
					setCapDMVFee(Boolean(d.capDMVFee));
					setCapDocFee(Boolean(d.capDocFee));
					setCapSalesTax(Boolean(d.capSalesTax));
					setCapMiscFee(Boolean(d.capMiscFee));
					setCapOtherfee(Boolean(d.capOtherfee));
					setCapTaxOnReduction(Boolean(d.capTaxOnReduction));
					setCapFirstPmt(Boolean(d.capFirstPmt));
					setCapWarranty(Boolean(d.capWarranty));
					setCapGap(Boolean(d.capGap));
					setCapSmogFees(Boolean(d.capSmogFees));

					setMoneyFactor(n(d.moneyFactor, 0));
					setAllowableMilesPerYear(n(d.allowableMilesPerYear, 0));
					setCapReduction(n(d.capReduction, 0));
					setBankFee(n(d.bankFee, 0));
					setTerminationFee(n(d.terminationFee, 0));
					setDispositionFee(n(d.dispositionFee, 0));
					setProRataRent(n(d.proRataRent, 0));
					setProRataSalesTax(n(d.proRataSalesTax, 0));
				},
				(err) => {
					console.log(err);
					// if (!err?.isGeneralError) {
					// 	showApiError(err, retrieveFailAlert);
					// }
				}
			);
		}
	}, []);

	const save = () => {
		console.log("Saving Lease Settings");
		const payload = {
			id: id,
			capBankFee,
			capDMVFee,
			capDocFee,
			capSalesTax,
			capMiscFee,
			capOtherfee,
			capTaxOnReduction,
			capFirstPmt,
			capWarranty,
			capGap,
			moneyFactor: n(moneyFactor),
			allowableMilesPerYear: n(allowableMilesPerYear),
			capReduction: n(capReduction),
			bankFee: n(bankFee),
			terminationFee: n(terminationFee),
			proRataRent: n(proRataRent),
			proRataSalesTax: n(proRataSalesTax),
			locationID: locationID,
			dealerID: dealerID,

			// remove if your API doesn't expect this
		};

		saveLeaseDefaults(payload).then(
			() => {
				console.log("saveLeaseDefaults", payload);
				saveSuccessAlert();
			},
			(err) => {
				if (!err?.isGeneralError) {
					showApiError(err, saveFailAlert);
				}
			}
		);
	};

	return (
		<div onKeyDown={formNavigationUtils}>
			<Form>
				<Card>
					<CardHeader>
						<CardTitle tag="h3">Lease Settings</CardTitle>
					</CardHeader>
					<Row className="m-0">
						<Col>
							<CardBody>
								<CheckBoxElement
									disableValidation
									checked={capBankFee}
									onChange={() => {
										if (isDeal) lease.editField("capBankFee", !capBankFee);
										setCapBankFee(!capBankFee);
									}}
									name="capBankFee"
									label="Add Bank Fee to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3"
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capDocFee}
									onChange={() => {
										if (isDeal) lease.editField("capDocFee", !capDocFee);
										setCapDocFee(!capDocFee);
									}}
									name="capDocFee"
									label="Add Doc Fee to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capMiscFee}
									onChange={() => {
										if (isDeal) lease.editField("capMiscFee", !capMiscFee);
										setCapMiscFee(!capMiscFee);
									}}
									name="capMiscFee"
									label="Add Misc Fee to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capOtherfee}
									onChange={() => {
										if (isDeal) lease.editField("capOtherfee", !capOtherfee);
										setCapOtherfee(!capOtherfee);
									}}
									name="capOtherfee"
									label="Add Other Fee to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capDMVFee}
									onChange={() => {
										if (isDeal) lease.editField("capDMVFee", !capDMVFee);
										setCapDMVFee(!capDMVFee);
									}}
									name="capDMVFee"
									label="Add DMV Fees to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3"
									bordered={false}
								/>
								<CheckBoxElement
									disableValidation
									checked={capSmogFees}
									onChange={() => {
										if (isDeal) lease.editField("capSmogFees", !capSmogFees);
										setCapSmogFees(!capSmogFees);
									}}
									name="capSmogFees"
									label="Add Smog Fees to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3"
									bordered={false}
								/>
								<CheckBoxElement
									disableValidation
									checked={capGap}
									onChange={() => {
										if (isDeal) lease.editField("capGap", !capGap);
										setCapGap(!capGap);
									}}
									name="capGap"
									label="Add GAP to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capWarranty}
									onChange={() => {
										if (isDeal) lease.editField("capWarranty", !capWarranty);
										setCapWarranty(!capWarranty);
									}}
									name="capWarranty"
									label="Add Warranty to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3"
									bordered={false}
								/>
								<CheckBoxElement
									disableValidation
									checked={capDispositionFee}
									onChange={() => {
										if (isDeal)
											lease.editField("capDispositionFee", !capDispositionFee);
										setCapDispositionFee(!capDispositionFee);
									}}
									name="capDispositionFee"
									label="Add Disposition Fee to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3"
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capSalesTax}
									onChange={() => {
										if (isDeal) lease.editField("capSalesTax", !capSalesTax);
										setCapSalesTax(!capSalesTax);
									}}
									name="capSalesTax"
									label="Add Sales Tax to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									disabled={!capSalesTax}
									checked={capTaxOnReduction}
									onChange={() => {
										if (isDeal)
											lease.editField("capTaxOnReduction", !capTaxOnReduction);
										setCapTaxOnReduction(!capTaxOnReduction);
									}}
									name="capTaxOnReduction"
									label="Cap Tax on Reduction"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capFirstPmt}
									onChange={() => {
										if (isDeal) lease.editField("capFirstPmt", !capFirstPmt);
										setCapFirstPmt(!capFirstPmt);
									}}
									name="capFirstPmt"
									label="Add First Payment to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>
							</CardBody>
						</Col>

						<Col>
							<CardBody>
								<IntegerInput
									disableValidation
									value={moneyFactor}
									onChange={(e) => {
										if (isDeal) {
											console.log("moneyFactor", e);
											lease.editField("moneyFactor", e);
										}
										setMoneyFactor(e);
									}}
									name="moneyFactor"
									label="Money Factor"
									type="number"
									colSize="col-sm-12"
									decimalScale={8}
								/>

								<IntegerInput
									disableValidation
									value={allowableMilesPerYear}
									onChange={(e) => {
										if (isDeal)
											lease.editField(
												"allowableMilesPerYear",
												e?.target?.value
											);
										setAllowableMilesPerYear(e?.target?.value);
									}}
									name="allowableMilesPerYear"
									label="Allowable Miles Per Year"
									type="number"
									colSize="col-sm-12"
								/>
								<CurrencyInput
									disableValidation
									value={additionalFee}
									onChange={(e) => {
										if (isDeal) lease.editField("additionalFee", e);
										setAdditionalFee(e);
									}}
									name="additionalFee"
									label="Periodic Fee"
									type="number"
									colSize="col-sm-12"
								/>
								<CurrencyInput
									disableValidation
									value={terminationFee}
									onChange={(e) => {
										if (isDeal) lease.editField("terminationFee", e);
										setTerminationFee(e);
									}}
									name="terminationFee"
									label="Termination Fee"
									type="number"
									colSize="col-sm-12"
								/>
								<CurrencyInput
									disableValidation
									value={dispositionFee}
									onChange={(e) => {
										if (isDeal) lease.editField("dispositionFee", e);
										setDispositionFee(e);
									}}
									name="dispositionFee"
									label="Disposition Fee"
									type="number"
									colSize="col-sm-12"
								/>
							</CardBody>
						</Col>
						{isDeal && (
							<Col>
								<CardBody>
									<CurrencyInput
										disableValidation
										value={proRataRent}
										onChange={(e) => {
											lease.editField("proRataRent", e);
											setProRataRent(e);
										}}
										name="proRataRent"
										label="Pro-Rata Rent"
										type="number"
										colSize="col-sm-12"
									/>
									<CurrencyInput
										disableValidation
										value={proRataSalesTax}
										onChange={(e) => {
											lease.editField("proRataSalesTax", e);
											setProRataSalesTax(e);
										}}
										name="proRataSalesTax"
										label="Pro-Rata SalesTax"
										type="number"
										colSize="col-sm-12"
									/>
									<CurrencyInput
										disableValidation
										value={securityDeposit}
										onChange={(e) => {
											lease.editField("securityDeposit", e);
											setSecurityDeposit(e);
										}}
										name="securityDeposit"
										label="Security Deposit"
										type="number"
										colSize="col-sm-12"
									/>
								</CardBody>
							</Col>
						)}
					</Row>
					{!isDeal && (
						<Row className="px-3 pb-3">
							<Button color="primary" onClick={save} size="md">
								<i className="nc-icon nc-cloud-download-93"></i> Save Default
							</Button>
						</Row>
					)}
				</Card>
			</Form>
		</div>
	);
};

export default LeaseSettings;
